/** @jsxImportSource @emotion/react */
import { TimeInput as MantineTimeInput } from '@mantine/dates';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTheme } from '../../../ThemeProvider';
import { Icon } from '../../Icon';

interface Props {
  value: Date | null;
  onChange: (v: Date) => void;
}

export const TimeInput = ({ value, onChange }: Props) => {
  const ref = useRef<
    | (HTMLInputElement & { showPicker: () => void; hidePicker: () => void })
    | null
  >(null);
  const [togglePicker, setTogglePicker] = useState(false);
  const { palette, spacing } = useTheme();

  const handleShow = useCallback(() => {
    ref.current?.showPicker();
    setTogglePicker(true);
  }, [ref]);

  const handleHide = useCallback(() => {
    setTogglePicker(false);
  }, []);

  useEffect(() => {
    if (!ref.current) return;
    if (value === null) {
      ref.current.value = '';
    }
  }, [ref, value]);

  return (
    <MantineTimeInput
      value={(value as Date | null)?.toTimeString().slice(0, 5)}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value && value) {
          const hours = Number(e.target.value.split(':')[0]);
          const minutes = Number(e.target.value.split(':')[1]);
          const dateValue = value as Date;
          const newValue = new Date();
          newValue.setTime(dateValue.getTime());
          newValue.setHours(hours, minutes);
          onChange && onChange(newValue);
        }
      }}
      ref={ref}
      rightSection={
        <div
          css={{
            transform: 'rotate(-90deg)',
            color: palette.grey.x500,
          }}
          onClick={togglePicker ? handleHide : handleShow}
          onBlur={handleHide}
        >
          <Icon name="chevron" size={spacing.xxs} />
        </div>
      }
      radius={'4px'}
      css={{
        '.mantine-TimeInput-input': {
          color: palette.grey.x500,
          fontWeight: '400',
          border: `1px solid ${palette.grey.x200}`,
        },
      }}
    />
  );
};
